import React, {useEffect, useMemo, useState, useCallback} from 'react'
import {
  Button,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import {useTranslation} from 'react-i18next'
import {formatPriceNumber} from 'helpers/uiHelper'
import {
  fetchListProductDetailUserApi,
  ProductPurchaseHistoryResponse,
  ProductUserDetailResponse,
} from 'api/productApi'
import PurchaseHistoryListStyle from 'pages/ProductHistory/PurchaseHistoryList/style'
import {
  statusList,
  TABLE_HEADER_PURCHASE_HISTORY,
  TYPE_GOOD_TYPE,
  TYPE_PAYMENT_STATUS,
  TypeGoodsType,
  UNIT_PRICES,
  USAGE_PERIOD_UNIT,
} from 'pages/ProductsPages/constant'
import {usdToCent} from 'helpers/paymentHelper'
import useProductManagement from 'features/productManagement/useProductManagement'
import CardStatus from './CardStatus'

interface IDataTableProps {
  onClickPurchaseDetail: (paymentId: string) => void
  listProductPurchaseHistory: ProductPurchaseHistoryResponse[]
}

const useStyles = makeStyles(PurchaseHistoryListStyle)
const DataTable = ({
  onClickPurchaseDetail,
  listProductPurchaseHistory,
}: IDataTableProps) => {
  const classes = useStyles()
  const {t} = useTranslation()
  const {getNameProduct, getPeriodProduct} = useProductManagement()

  const [listDetailProduct, setListDetailProduct] = useState<
    ProductUserDetailResponse[]
  >([])
  const [statusChoose, setStatusChoose] = useState<string>('ALL')

  const getProductCategory = (goodsType: TypeGoodsType) => {
    switch (goodsType) {
      case TYPE_GOOD_TYPE.CHARGE:
        return t('IChargeV1')
      case TYPE_GOOD_TYPE.PACKAGE:
        return t('IPackage')
      default:
        return t('IPackage')
    }
  }

  const getPackageName = useCallback(
    (goodsId: string) => {
      if (!listDetailProduct || listDetailProduct.length === 0) return ''
      const product = listDetailProduct.find(
        (item) => item.goods.goodsId === goodsId,
      )
      if (product) {
        return getNameProduct(product.goods.goodsName).name
      }
      return ''
    },
    [listDetailProduct, getNameProduct],
  )

  const getPackagePeriod = useCallback(
    (goodsId: string) => {
      if (!listDetailProduct || listDetailProduct.length === 0) return ''
      const product = listDetailProduct.find(
        (item) => item.goods.goodsId === goodsId,
      )
      if (product) {
        return product.offer.usagePeriodUnit === USAGE_PERIOD_UNIT.LIFETIME
          ? t('IUnlimited')
          : `${product.offer.usagePeriod || ''} ${t('IMonths')}`
      }
      return ''
    },
    [listDetailProduct, getPeriodProduct],
  )

  useEffect(() => {
    const fetchData = async () => {
      const ids = listProductPurchaseHistory.map(
        (item) => item.order.orderItems[0].goodsId,
      )
      const results = await fetchListProductDetailUserApi(ids)
      setListDetailProduct(results.data)
    }

    if (listProductPurchaseHistory.length > 0) {
      fetchData()
    }
  }, [listProductPurchaseHistory])

  const handleFilterStatus = (status: string) => {
    setStatusChoose(status)
  }

  const filteredList = useMemo(() => {
    const sortedList = [...listProductPurchaseHistory].sort((a, b) => {
      return (
        new Date(b.order.orderAt).getTime() -
        new Date(a.order.orderAt).getTime()
      )
    })

    if (statusChoose === 'ALL') return sortedList

    if (statusChoose === TYPE_PAYMENT_STATUS.PENDING) {
      return sortedList.filter((item) =>
        item.payment.status.includes(TYPE_PAYMENT_STATUS.PENDING),
      )
    }
    return sortedList.filter((item) => item.payment.status === statusChoose)
  }, [listProductPurchaseHistory, statusChoose])

  const getStatusRecord = (status: string) => {
    switch (status) {
      case TYPE_PAYMENT_STATUS.PENDING:
        return t('IWriting')
      case TYPE_PAYMENT_STATUS.APPROVED:
        return t('IComplete')
      case TYPE_PAYMENT_STATUS.REFUNDED:
        return t('ICancel')
      default:
        return t('IWriting')
    }
  }

  return (
    <div className={classes.listCard}>
      <div className={classes.listCardStatus}>
        {statusList.map((item) => (
          <button
            key={item.value}
            type='button'
            style={{flex: 1}}
            onClick={() => handleFilterStatus(item.value)}
          >
            <CardStatus
              value={item.value}
              status={t(item.label)}
              list={listProductPurchaseHistory}
            />
          </button>
        ))}
      </div>
      <Table
        size='small'
        className={classes.table}
        style={{tableLayout: 'fixed'}}
      >
        <TableHead className={classes.tableHeaderData}>
          {TABLE_HEADER_PURCHASE_HISTORY(t).map((item, index) => (
            <TableCell
              align={index === 0 ? 'left' : 'center'}
              padding='checkbox'
              key={item.id}
              className={classes.headerMax}
              style={{width: item.width || 120}}
            >
              {item.label}
            </TableCell>
          ))}
        </TableHead>
        <TableBody>
          {filteredList.map((item) => (
            <TableRow
              key={item.order.orderId}
              className={classes.rowDataTable}
              hover
            >
              <TableCell
                align='center'
                padding='checkbox'
                onClick={() => {
                  onClickPurchaseDetail(item.payment.paymentId)
                }}
              >
                <div className={classes.orderHeader}>
                  <div className={classes.orderName}>
                    {getPackageName(item?.order?.orderItems[0]?.goodsId ?? '')}
                  </div>
                  <div className={classes.orderId}>
                    {t('IOrderNo')}: {item.payment.paymentNo}
                  </div>
                </div>
              </TableCell>
              <TableCell align='center' padding='checkbox'>
                {getProductCategory(
                  item?.order?.orderItems[0]?.goodsType ?? '',
                )}
              </TableCell>
              <TableCell align='center' padding='checkbox'>
                {getPackagePeriod(item?.order?.orderItems[0]?.goodsId ?? '')}
              </TableCell>
              <TableCell align='center' padding='checkbox'>
                {item.payment.price.currency}{' '}
                <span>
                  {formatPriceNumber(
                    item.payment.price.currency === UNIT_PRICES.USD
                      ? usdToCent(item.payment.price.price)
                      : item.payment.price.price,
                  )}
                </span>
              </TableCell>
              <TableCell align='center' padding='checkbox'>
                <div className={classes.listBtn}>
                  <Button
                    color='default'
                    variant='outlined'
                    size='small'
                    onClick={() =>
                      onClickPurchaseDetail(item.payment.paymentId)
                    }
                  >
                    {getStatusRecord(item.payment.status)}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default DataTable
